import { createListenerMiddleware } from '@reduxjs/toolkit';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { creatorActions } from '../slices/creatorSlice';
import { RootState, AppDispatch } from '..';
import { RendererMode } from '@/renderer/types';
import {
	CreatorOptions,
	CreatorStep,
	PlaygroundShapeType,
} from '@/types/creator';
import { tutorialActions } from '../slices/tutorialSlice';
import shapes from '@/configs/shapes';

const renderer = ProjectRenderer.getInstance();

function syncStateToRendereMode(options: CreatorOptions) {
	if (options.isPreviewMode) {
		renderer.mode = RendererMode.PREVIEW;
		return;
	}

	if (options.isMeasuring) {
		renderer.mode = RendererMode.MEASURING;
		return;
	}

	switch (options.currentStep) {
		case CreatorStep.SHAPES:
			renderer.mode = RendererMode.SHAPES;
			break;
		case CreatorStep.MODULES:
			renderer.mode = RendererMode.MODULES;
			break;
		default:
			renderer.mode = RendererMode.PREVIEW;
			break;
	}
}

export const creatorListenerMiddleware = createListenerMiddleware();

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectShape,
	effect: ({ payload }, listenerApi) => {
		const shape = shapes.find((shape) => shape.id === payload);
		if (!shape) return;

		if (shape.type !== PlaygroundShapeType.CUSTOM)
			listenerApi.dispatch(tutorialActions.enableTutorialStep([2, 3]));
		else
			listenerApi.dispatch(
				creatorActions.updateOptions({ isMobileAsideOpened: false })
			);

		renderer.playgroundManager.setFromShape(shape);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setCreatorStep,
	effect: ({ payload }, listenerApi) => {
		const { currentStep } = listenerApi.getState().creator.options;

		if (currentStep === CreatorStep.MODULES)
			listenerApi.dispatch(tutorialActions.enableTutorialStep(4));

		renderer.handleCreatorStepChange(currentStep);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setActiveCamera,
	effect: ({ payload }, listenerApi) => {
		renderer.cameraController.activeCamera = payload;
		syncStateToRendereMode(listenerApi.getState().creator.options);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectPlaygroundFilling,
	effect: ({ payload }, listenerApi) => {
		if (!payload) return;

		renderer.playgroundManager.floor.setCustomMaterial(payload.asset);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.addCreatorProductModule,
	effect: ({ payload }, listenerApi) => {
		renderer.modulesManager.addProductModule({
			refId: payload.refId,
			code: payload.productModule.code,
			asset: payload.productModule.asset,
		});

		listenerApi.dispatch(tutorialActions.enableTutorialStep([5, 6, 7, 8]));
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeCreatorProductModule,
	effect: ({ payload }, listenerApi) => {
		renderer.modulesManager.removeProductModule(payload);

		listenerApi.dispatch(
			creatorActions.updateOptions({
				moduleContextMenuData: {
					moduleRefId: null,
					position: [0, 0],
					type: 'product',
				},
			})
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.addCreatorLandscapeModule,
	effect: ({ payload }, listenerApi) => {
		renderer.modulesManager.addLandscapeModule(payload);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setMeasuring,
	effect: ({ payload }, listenerApi) => {
		syncStateToRendereMode(listenerApi.getState().creator.options);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeCreatorLandscapeModule,
	effect: ({ payload }, listenerApi) => {
		renderer.modulesManager.removeLandscapeModule(payload);

		listenerApi.dispatch(
			creatorActions.updateOptions({
				moduleContextMenuData: {
					moduleRefId: null,
					position: [0, 0],
					type: 'product',
				},
			})
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.loadProject,
	effect: async ({ payload }, listenerApi) => {
		if (payload.configuration.options.state.options.isPreviewMode)
			renderer.mode = RendererMode.PREVIEW;

		renderer.loadFromData(payload.configuration.options.renderer);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.copyProject,
	effect: async ({ payload }, listenerApi) => {
		renderer.mode = RendererMode.SHAPES;
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeMeasurement,
	effect: async ({ payload }, listenerApi) => {
		renderer.measurementManager.handleRemoveMeasure(payload);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.addScreenshotCamera,
	effect: async ({ payload }, listenerApi) => {
		renderer.cameraController.addUserScreenshotCamera(payload);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeScreenshotCamera,
	effect: async ({ payload }, listenerApi) => {
		renderer.cameraController.removeUserScreenshotCamera(payload);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.reorderScreenshotCameras,
	effect: async ({ payload }, listenerApi) => {
		renderer.cameraController.syncUserScreenshotCameras(
			listenerApi.getState().creator.options.screenshotCameras
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.handleCameraUptate,
	effect: async ({ payload }, listenerApi) => {
		renderer.cameraController.syncUserScreenshotCameras(
			listenerApi.getState().creator.options.screenshotCameras
		);
	},
});
